// src/services/insurance-eligibility.service.ts

import type {
  Claim,
  EligibilityCheck,
  Policy,
} from "../contexts/InsuranceContextType";

interface CheckEligibilityParams {
  patientId: string;
  policyNumber: string;
  policies: Policy[];
  claims: Claim[];
}

export interface EligibilityCheckResult {
  result: EligibilityCheck | null;
  error?: "policy_not_found";
}

const calculateCoveredClaimAmount = (
  claim: Claim
): number => {
  if (claim.coveragePercentage == null) {
    return claim.claimAmount;
  }

  return (
    claim.claimAmount *
    (claim.coveragePercentage / 100)
  );
};

export function checkInsuranceEligibility({
  patientId,
  policyNumber,
  policies,
  claims,
}: CheckEligibilityParams): EligibilityCheckResult {
  const policy = policies.find(
    (item) =>
      item.policyNumber === policyNumber
  );

  if (!policy) {
    return {
      result: null,
      error: "policy_not_found",
    };
  }

  const now = new Date();
  const policyEndDate = new Date(
    policy.endDate
  );

  const isActive =
    policy.status === "active";

  const isNotExpired =
    !Number.isNaN(
      policyEndDate.getTime()
    ) && policyEndDate >= now;

  const approvedClaims = claims.filter(
    (claim) =>
      claim.policyNumber ===
        policyNumber &&
      claim.status === "approved"
  );

  const usedCoverage =
    approvedClaims.reduce(
      (total, claim) =>
        total +
        calculateCoveredClaimAmount(
          claim
        ),
      0
    );

  const remainingCoverage =
    Math.max(
      0,
      policy.coverageAmount -
        usedCoverage
    );

  const deductible =
    policy.deductible ?? 0;

  const deductibleMet =
    Math.min(
      usedCoverage,
      deductible
    );

  const deductibleRemaining =
    Math.max(
      0,
      deductible -
        deductibleMet
    );

  const timestamp =
    new Date().toISOString();

  return {
    result: {
      id: `ELIG-${Date.now()}`,
      patientName:
        policy.policyHolder,
      patientId,
      policyNumber,
      checkDate: timestamp,
      isEligible:
        isActive &&
        isNotExpired &&
        remainingCoverage > 0,
      coverageStatus:
        isActive && isNotExpired
          ? "Active"
          : "Inactive",
      coverageAmount:
        policy.coverageAmount,
      remainingCoverage,
      deductibleMet,
      deductibleRemaining,
    },
  };
}